import {
	ATTR_FLAG,
	S_IFMT,
	S_IFREG,
	S_IFDIR,
	S_IFCHR,
	S_IFBLK,
	S_IFIFO,
	S_IFLNK,
	S_IFSOCK,
} from './constants.js';

// ── ATTRS: wire-level attribute object ─────────────────────────────

export type Attrs = {
	mode?: number;
	uid?: number;
	gid?: number;
	size?: number;
	atime?: number;
	mtime?: number;
};

// ── Stats: ATTRS with type-checking helper methods ─────────────────

export class Stats {
	readonly mode: number;
	readonly uid: number;
	readonly gid: number;
	readonly size: number;
	readonly atime: Date;
	readonly mtime: Date;

	constructor(attrs: Attrs) {
		this.mode = attrs.mode ?? 0;
		this.uid = attrs.uid ?? 0;
		this.gid = attrs.gid ?? 0;
		this.size = attrs.size ?? 0;
		this.atime = new Date((attrs.atime ?? 0) * 1000);
		this.mtime = new Date((attrs.mtime ?? 0) * 1000);
	}

	isDirectory(): boolean {
		return (this.mode & S_IFMT) === S_IFDIR;
	}

	isFile(): boolean {
		return (this.mode & S_IFMT) === S_IFREG;
	}

	isBlockDevice(): boolean {
		return (this.mode & S_IFMT) === S_IFBLK;
	}

	isCharacterDevice(): boolean {
		return (this.mode & S_IFMT) === S_IFCHR;
	}

	isSymbolicLink(): boolean {
		return (this.mode & S_IFMT) === S_IFLNK;
	}

	isFIFO(): boolean {
		return (this.mode & S_IFMT) === S_IFIFO;
	}

	isSocket(): boolean {
		return (this.mode & S_IFMT) === S_IFSOCK;
	}
}

// ── Directory entry (from READDIR responses) ───────────────────────

export type DirectoryEntry = {
	filename: string;
	longname: string;
	attrs: Stats;
};

// ── Encode/decode ATTRS for the SFTP wire protocol ─────────────────

export function encodeAttrs(attrs: Attrs): Uint8Array {
	let flags = 0;
	const parts: Uint8Array[] = [];

	if (attrs.size !== undefined) {
		flags |= ATTR_FLAG.SIZE;
		const buf = new Uint8Array(8);
		const view = new DataView(buf.buffer);
		// size is uint64 — split into high and low 32 bits
		view.setUint32(0, Math.floor(attrs.size / 0x100000000) >>> 0);
		view.setUint32(4, attrs.size >>> 0);
		parts.push(buf);
	}

	if (attrs.uid !== undefined && attrs.gid !== undefined) {
		flags |= ATTR_FLAG.UIDGID;
		const buf = new Uint8Array(8);
		const view = new DataView(buf.buffer);
		view.setUint32(0, attrs.uid);
		view.setUint32(4, attrs.gid);
		parts.push(buf);
	}

	if (attrs.mode !== undefined) {
		flags |= ATTR_FLAG.PERMISSIONS;
		const buf = new Uint8Array(4);
		new DataView(buf.buffer).setUint32(0, attrs.mode);
		parts.push(buf);
	}

	if (attrs.atime !== undefined && attrs.mtime !== undefined) {
		flags |= ATTR_FLAG.ACMODTIME;
		const buf = new Uint8Array(8);
		const view = new DataView(buf.buffer);
		view.setUint32(0, attrs.atime);
		view.setUint32(4, attrs.mtime);
		parts.push(buf);
	}

	// flags header (4 bytes)
	const header = new Uint8Array(4);
	new DataView(header.buffer).setUint32(0, flags);

	const total = 4 + parts.reduce((s, p) => s + p.length, 0);
	const result = new Uint8Array(total);
	result.set(header, 0);
	let offset = 4;
	for (const part of parts) {
		result.set(part, offset);
		offset += part.length;
	}
	return result;
}

export function decodeAttrs(
	data: Uint8Array,
	startOffset = 0,
): { attrs: Attrs; bytesRead: number } {
	const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
	let offset = startOffset;
	const flags = view.getUint32(offset);
	offset += 4;

	const attrs: Attrs = {};

	if (flags & ATTR_FLAG.SIZE) {
		const high = view.getUint32(offset);
		const low = view.getUint32(offset + 4);
		attrs.size = high * 0x100000000 + low;
		offset += 8;
	}

	if (flags & ATTR_FLAG.UIDGID) {
		attrs.uid = view.getUint32(offset);
		attrs.gid = view.getUint32(offset + 4);
		offset += 8;
	}

	if (flags & ATTR_FLAG.PERMISSIONS) {
		attrs.mode = view.getUint32(offset);
		offset += 4;
	}

	if (flags & ATTR_FLAG.ACMODTIME) {
		attrs.atime = view.getUint32(offset);
		attrs.mtime = view.getUint32(offset + 4);
		offset += 8;
	}

	if (flags & ATTR_FLAG.EXTENDED) {
		const count = view.getUint32(offset);
		offset += 4;
		for (let i = 0; i < count; i++) {
			// skip extended type name
			const nameLen = view.getUint32(offset);
			offset += 4 + nameLen;
			// skip extended data
			const dataLen = view.getUint32(offset);
			offset += 4 + dataLen;
		}
	}

	return { attrs, bytesRead: offset - startOffset };
}
